import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { Button } from './Button';
import { CadrePleinEcran } from './CadrePleinEcran';
import { CircleAlert, X } from './icons';
import { SEUIL_OFFRE_ELOIGNEE } from '../config/offres';
import { updateProfile } from '../services/profile';
import { maskBirthDate, parseBirthDate } from '../utils/birthDate';
import { t } from '../i18n';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';

export function avantPreparationNecessaire(score: number, naissanceConnue: boolean): boolean {
  return !naissanceConnue || score < SEUIL_OFFRE_ELOIGNEE;
}

export interface AvantPreparationProps {
  score: number;
  naissanceConnue: boolean;
  onNaissance: (iso: string) => void;
  onContinuer: () => void;
  onAnnuler: () => void;
}

/**
 * Ce qui se règle AVANT tout appel à l'IA, donc avant qu'un crédit puisse être utilisé (décisions
 * du 03/10/2026) : la date de naissance si elle manque — sans elle, ni le ton de la lettre ni les
 * règles pour les moins de 18 ans ne s'appliquent —, puis l'avertissement sous 50.
 */
export function AvantPreparation({ score, naissanceConnue, onNaissance, onContinuer, onAnnuler }: AvantPreparationProps) {
  const [etape, setEtape] = useState<'naissance' | 'avertissement'>(naissanceConnue ? 'avertissement' : 'naissance');
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const enregistrer = async () => {
    const r = parseBirthDate(saisie);
    if ('error' in r) return setErreur(r.error);
    setEnvoi(true);
    setErreur(null);
    try {
      await updateProfile({ birth_date: r.iso });
      onNaissance(r.iso);
      if (score < SEUIL_OFFRE_ELOIGNEE) setEtape('avertissement');
      else onContinuer();
    } catch {
      setErreur(t('offres.avant.erreurNaissance'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CadrePleinEcran fond={colors.grey50} testID="avant-preparation">
      <LinearGradient colors={[colors.night, colors.navyDeep]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={styles.barre}>
        <Pressable accessibilityRole="button" accessibilityLabel={t('offres.avant.fermer')} onPress={onAnnuler} style={styles.rond}>
          <X size={22} color={colors.white} />
        </Pressable>
      </LinearGradient>
      <ScrollView contentContainerStyle={styles.contenu} keyboardShouldPersistTaps="handled">
        {etape === 'naissance' ? (
          <>
            <Text style={styles.titre} accessibilityRole="header">
              {t('offres.avant.naissanceTitre')}
            </Text>
            <Text style={styles.texte}>{t('offres.avant.naissanceRaison')}</Text>
            <TextInput
              value={saisie}
              onChangeText={(v) => setSaisie(maskBirthDate(v))}
              placeholder={t('offres.avant.naissanceFormat')}
              keyboardType="number-pad"
              style={styles.champ}
              testID="avant-naissance"
              accessibilityLabel={t('offres.avant.naissanceTitre')}
            />
            {erreur ? <Text style={styles.erreur}>{erreur}</Text> : null}
            <Button title={t('offres.avant.continuer')} variant="dark" loading={envoi} onPress={() => void enregistrer()} />
            <Button title={t('offres.avant.annuler')} variant="secondary" onPress={onAnnuler} />
          </>
        ) : (
          <>
            <View style={styles.alerte}>
              <CircleAlert size={22} color={colors.corail} />
              <Text style={styles.titre} accessibilityRole="header">
                {t('offres.avant.eloigneeTitre')}
              </Text>
            </View>
            <Text style={styles.texte}>{t('offres.avant.eloigneeTexte', { score })}</Text>
            <Button title={t('offres.avant.prepareQuandMeme')} variant="dark" onPress={onContinuer} />
            <Button title={t('offres.avant.nePasPreparer')} variant="secondary" onPress={onAnnuler} />
          </>
        )}
      </ScrollView>
    </CadrePleinEcran>
  );
}

const styles = StyleSheet.create({
  barre: { flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: screenPadding, paddingVertical: spacing.sm },
  rond: { minWidth: 44, minHeight: 44, borderRadius: radius.pill, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.inkSoft },
  contenu: { padding: screenPadding, paddingTop: spacing.xl, gap: spacing.lg },
  alerte: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  titre: { flexShrink: 1, fontSize: typography.h2.size, lineHeight: typography.h2.lineHeight, fontWeight: typography.h2.weight, color: colors.navy },
  texte: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.night },
  champ: {
    minHeight: 48,
    paddingHorizontal: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.grey200,
    backgroundColor: colors.white,
    fontSize: typography.body.size,
    color: colors.night,
  },
  erreur: { fontSize: typography.small.size, color: colors.error },
});
